import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const ConfirmContext = createContext(null);

function FocusTrap({ children, containerRef }) {
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const focusable = node.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handler = (e) => {
      if (e.key !== 'Tab' || focusable.length === 0) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    node.addEventListener('keydown', handler);
    return () => node.removeEventListener('keydown', handler);
  }, [containerRef]);
  return children;
}

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, body, danger, confirmLabel, cancelLabel, resolver }
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);

  const confirm = useCallback(
    (opts = {}) =>
      new Promise((resolve) => {
        setState({
          title: opts.title || 'Confirm',
          body: opts.body || '',
          danger: !!opts.danger,
          confirmLabel: opts.confirmLabel || 'Confirm',
          cancelLabel: opts.cancelLabel || 'Cancel',
          resolver: resolve,
        });
      }),
    []
  );

  const resolve = useCallback((result) => {
    setState((current) => {
      if (current?.resolver) current.resolver(result);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!state) return undefined;
    previouslyFocused.current = document.activeElement;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        resolve(false);
      } else if (e.key === 'Enter') {
        const target = e.target;
        const isButton = target && target.tagName === 'BUTTON';
        if (!isButton) {
          e.preventDefault();
          resolve(true);
        }
      }
    };
    document.addEventListener('keydown', onKey);

    const confirmBtn = dialogRef.current?.querySelector('[data-confirm-button]');
    if (confirmBtn) confirmBtn.focus();

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = originalOverflow;
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [state, resolve]);

  const confirmColor = state?.danger ? '#A61E17' : '#FF4D00';
  const confirmHover = state?.danger ? '#8B1912' : '#E63E00';

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60"
          onClick={() => resolve(false)}
        >
          <FocusTrap containerRef={dialogRef}>
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-dialog-title"
              aria-describedby="confirm-dialog-body"
              className="bg-[#1E1E1E] border border-[#2A2A2A] w-full max-w-md mx-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-[#2A2A2A]">
                <h2
                  id="confirm-dialog-title"
                  className="text-base font-bold uppercase tracking-wider font-['Barlow_Condensed'] text-[#F5F1E8]"
                >
                  {state.title}
                </h2>
              </div>
              {state.body && (
                <div
                  id="confirm-dialog-body"
                  className="px-5 py-4 text-sm text-[#F5F1E8]/90"
                >
                  {state.body}
                </div>
              )}
              <div className="px-5 py-3 flex justify-end gap-2 border-t border-[#2A2A2A]">
                <button
                  type="button"
                  onClick={() => resolve(false)}
                  className="px-4 py-2 min-h-[36px] text-xs font-bold uppercase tracking-wider font-['Barlow_Condensed'] border border-[#2A2A2A] text-[#F5F1E8] hover:border-[#F5F1E8] transition-colors"
                >
                  {state.cancelLabel}
                </button>
                <button
                  type="button"
                  data-confirm-button
                  onClick={() => resolve(true)}
                  className="px-4 py-2 min-h-[36px] text-xs font-bold uppercase tracking-wider font-['Barlow_Condensed'] text-[#0A0A0A] transition-colors"
                  style={{ backgroundColor: confirmColor }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = confirmHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = confirmColor; }}
                >
                  {state.confirmLabel}
                </button>
              </div>
            </div>
          </FocusTrap>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return ctx;
}
